import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Crown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import SubscriptionPlans from "../features/subscription/SubscriptionPlans";
import { SubscriptionState } from "../features/subscription/contracts/SubscriptionState";
import {
  selectSubscriptionCheckoutStatus,
  selectSubscriptionPlans,
  selectSubscriptionStatus,
} from "../features/subscription/subscription-slice";
import {
  fetchSubscriptionStatus,
  fetchSubscriptionPlans,
  openBillingPortal,
} from "../features/subscription/slice-extensions/subscription-slice-extension";
import { useSubscriptionCheckout } from "../features/subscription/useSubscriptionCheckout";
import { selectIsOrgOwner } from "../features/authentication/authenticationSlice";
import { useColleagues } from "../features/instructors/hooks/useColleagues";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(date);

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectSubscriptionStatus);
  const plans = useAppSelector(selectSubscriptionPlans);
  const checkoutStatus = useAppSelector(selectSubscriptionCheckoutStatus);
  const isOrgOwner = useAppSelector(selectIsOrgOwner);
  const { activeColleagues } = useColleagues();
  const { handleSubscribe } = useSubscriptionCheckout();

  useEffect(() => {
    dispatch(fetchSubscriptionStatus());
    dispatch(fetchSubscriptionPlans());
  }, [dispatch]);

  const stateLine = () => {
    if (!status) return null;
    if (status.coveredByOrganization) {
      return t("subscription.page.stateCoveredByOrg", {
        name: status.organizationName ?? t("subscription.page.yourOrganization"),
      });
    }
    switch (status.state) {
      case SubscriptionState.Trialing:
        return t("subscription.page.stateTrialing", { count: status.trialDaysRemaining });
      case SubscriptionState.Active:
        return t("subscription.page.stateActive");
      case SubscriptionState.Cancelled:
        return t("subscription.page.stateCancelled");
      default:
        return t("subscription.page.stateReadOnly");
    }
  };

  const handleManageBilling = async () => {
    try {
      const url = await dispatch(openBillingPortal()).unwrap();
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error(t("subscription.page.portalFailed"));
    }
  };

  const checkoutBusy = checkoutStatus === LoadingStatus.Loading;

  return (
    <div className="flex h-full gap-4">
      <LeftMenu />
      <div className="w-full p-4 pb-10 lg:px-10">
        <div className="lg:max-w-[960px]">
          <Breadcrumbs />
          <div className="py-6">
            <h1>{t("subscription.page.title")}</h1>

            <div className="mt-4 lg:mt-6 with-border rounded-2xl p-4 lg:p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
                  <Crown size={20} strokeWidth={2} />
                </span>
                <div>
                  <p className="font-semibold text-grey-20 dark:text-white">{stateLine()}</p>
                  {status?.isTrialing && (
                    <p className="text-sm text-grey-50 dark:text-grey-70">
                      {t("subscription.page.trialEndsOn", { date: formatDate(status.trialEndsAt) })}
                    </p>
                  )}
                  {status?.complimentaryUntil && (
                    <p className="text-sm text-grey-50 dark:text-grey-70">
                      {t("subscription.page.complimentaryUntil", {
                        date: formatDate(status.complimentaryUntil),
                      })}
                    </p>
                  )}
                  {status?.isReadOnly && isOrgOwner && (
                    <p className="text-sm text-grey-50 dark:text-grey-70">
                      {t("subscription.page.readOnlyExplainer")}
                    </p>
                  )}
                </div>
              </div>

              {status?.hasProviderSubscription && (
                <div className="mt-4 border-t border-grey-90 dark:border-grey-30 pt-4">
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    className="btn btn-secondary"
                  >
                    {t("subscription.page.manageButton")}
                  </button>
                  <p className="mt-2 text-sm text-grey-50 dark:text-grey-70">
                    {t("subscription.page.manageHint")}
                  </p>
                </div>
              )}
            </div>

            {status && status.state !== SubscriptionState.Active &&
              !status.coveredByOrganization &&
              (isOrgOwner ? (
                <SubscriptionPlans
                  status={status}
                  plans={plans}
                  onSubscribe={handleSubscribe}
                  busy={checkoutBusy}
                  extraSeatCount={Math.max(0, activeColleagues.length - 1)}
                />
              ) : (
                <p className="mt-6 text-sm text-grey-50 dark:text-grey-70">
                  {t("subscription.page.askOwnerToSubscribe")}
                </p>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
